import { useEffect, useRef, useState } from "react";
import constate from "constate";
import axios from "axios";

const [ListingListProvider, useListingList] = constate(() => {
  const [searchQuery, setSearchQuery] = useState("");
  // Match DAO Watches' Manage Listings default — refreshed_at bumps edited
  // listings to the top, which is what we want for inventory work.
  const [sort, setSort] = useState("refreshed_at|desc");
  const [filter, setFilter] = useState(null);
  const [inStock, setInStock] = useState(false);
  const [isConsignment, setConsignment] = useState(false);
  const [sold, setSold] = useState(false);
  const [count, setCount] = useState(null);
  // Value of the listings currently in scope, in GBP pence (what the backend
  // sums). null = not known yet / not applicable — never render it as 0, which
  // would read as "this stock is worth nothing".
  const [stockValue, setStockValue] = useState(null);
  const [stockBuyingValue, setStockBuyingValue] = useState(null);
  const valueRequestRef = useRef(0);

  const onSearchQueryChange = (query) => {
    setSearchQuery(query);
  };

  // New function to handle advanced filters from shop page
  const onAdvancedFiltersChange = (advancedFilters) => {
    console.log("onAdvancedFiltersChange called with:", advancedFilters);
    console.log("Current filter state:", filter);

    // If advancedFilters is null, clear all filters
    if (advancedFilters === null) {
      console.log("Clearing all filters");
      setFilter(null);
      return;
    }

    // Check if this is a filter removal operation
    // If the new filters have fewer keys than current filters, it's likely a removal
    const isRemoval =
      filter &&
      Object.keys(advancedFilters).length < Object.keys(filter).length;

    if (isRemoval) {
      console.log("Detected filter removal, replacing filter state");
      setFilter(advancedFilters);
      return;
    }

    // If current filter is null, just set the new filters
    if (filter === null) {
      console.log("Setting new filters:", advancedFilters);
      setFilter(advancedFilters);
      return;
    }

    // Merge advanced filters with existing filter state (for adding new filters)
    const mergedFilters = {
      ...filter,
      ...advancedFilters,
    };

    // Remove empty filters
    Object.keys(mergedFilters).forEach((key) => {
      if (
        mergedFilters[key] === null ||
        mergedFilters[key] === undefined ||
        (Array.isArray(mergedFilters[key]) && mergedFilters[key].length === 0)
      ) {
        delete mergedFilters[key];
      }
    });

    // Handle special cases for existing ListingsFilters component
    if (mergedFilters.is_reserved) {
      delete mergedFilters.is_reserved;
      mergedFilters.status = ["reserved"];
    }

    if (mergedFilters.with_deposit) {
      delete mergedFilters.with_deposit;
      mergedFilters.status = ["deposit"];
    }

    const finalFilters =
      Object.keys(mergedFilters).length > 0 ? mergedFilters : null;
    console.log("Setting final filters:", finalFilters);
    setFilter(finalFilters);
  };

  const listParams = {
    query: searchQuery,
    onResultsLoaded: (_, cnt) => setCount(cnt),
  };

  if (sort) {
    const sortArr = sort.split("|");
    listParams.sort = { field: sortArr[0], direction: sortArr[1] };
  }

  if (filter) {
    listParams.filters = filter;
  }

  if (inStock && isConsignment) {
    listParams.filters = {
      ...listParams.filters,
      in_stock: ["owned", "exclusive"],
    };
  } else if (inStock) {
    listParams.filters = {
      ...listParams.filters,
      in_stock: ["owned"],
    };
  } else if (isConsignment) {
    listParams.filters = {
      ...listParams.filters,
      in_stock: ["exclusive"],
    };
  } else {
    listParams.filters = {
      ...listParams.filters,
      in_stock: undefined,
    };
  }

  // SOLD pill: always send an explicit flag so the backend hides sold listings
  // by default (sold=0) and shows ONLY sold listings when the pill is ON
  // (sold=1). Consignment/In-Stock still narrow within the sold set via the
  // in_stock filter above.
  listParams.filters = {
    ...listParams.filters,
    sold: sold ? 1 : 0,
  };

  // Stock value is only surfaced while a stock pill is on (In Stock /
  // Consignment) — the two views where "what is this worth" is the question —
  // so it is only fetched then.
  const showsStockValue = inStock || isConsignment;

  // /listings/search/value takes the SAME body as the grid's own search, so the
  // total always describes exactly the listings the grid counted: search text,
  // advanced filters, both stock pills and SOLD all narrow it identically.
  // Serialising the scope is what makes that dependable — listParams is rebuilt
  // every render, so an object dep would refetch on every keystroke elsewhere
  // on the page (and a `filters.status`-only dep, as before, missed every other
  // pill: toggling SOLD left a stale total on screen).
  const valueScope = showsStockValue
    ? JSON.stringify({ query: searchQuery, filters: listParams.filters })
    : null;

  useEffect(() => {
    // Bump first, so an in-flight response from the previous scope is discarded
    // rather than landing on top of the new one.
    valueRequestRef.current += 1;
    const requestId = valueRequestRef.current;

    if (!valueScope) {
      setStockValue(null);
      setStockBuyingValue(null);
      return;
    }

    const { query, filters } = JSON.parse(valueScope);

    axios
      .post("/api/listings/search/value", { list: "uploaded", query, filters })
      .then(({ data }) => {
        if (valueRequestRef.current !== requestId) return; // stale — discard
        setStockValue(data?.listingValue ?? null);
        // null for non-admins: the backend masks cost data rather than zeroing it.
        setStockBuyingValue(data?.buyingValue ?? null);
      })
      .catch((err) => {
        if (valueRequestRef.current !== requestId) return;
        console.error(
          "[RC] stock value fetch failed:",
          err?.response?.status,
          err?.response?.data?.message ?? err?.message
        );
        setStockValue(null);
        setStockBuyingValue(null);
      });
  }, [valueScope]);

  return {
    onSearchQueryChange,
    onAdvancedFiltersChange,
    sort,
    setSort,
    filter,
    setFilter,
    inStock,
    setInStock,
    isConsignment,
    setConsignment,
    sold,
    setSold,
    count,
    showsStockValue,
    // GBP pence — for callers that format money themselves (core/currency).
    stockValue,
    stockBuyingValue,
    // Major units, for the legacy StockCounter's NumberFormat props.
    totalListingPrice: (stockValue ?? 0) / 100,
    totalBuyingPrice: (stockBuyingValue ?? 0) / 100,
    listParams,
  };
});

export { ListingListProvider, useListingList };
